/**
 * React Query hooks for position thesis operations.
 *
 * Refactored from position comments to support thesis fields
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPositionTheses,
  createPositionThesis,
  updatePositionThesis,
  deletePositionThesis,
  getWatchlistThesis,
  createWatchlistThesis,
  type PositionThesis,
} from '../api/positionThesisApi';
import type { WatchlistThesisCreateRequest } from '../api/positionThesisApi';

/**
 * Hook to fetch thesis for a position.
 * Returns null if no thesis exists (not an error).
 */
export const usePositionTheses = (positionId: number) => {
  return useQuery({
    queryKey: ['positionThesis', positionId],
    queryFn: () => getPositionTheses(positionId),
    enabled: !!positionId,
  });
};

/**
 * Hook to add a new thesis to a position.
 */
export const useAddPositionThesis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      positionId,
      thesisText,
      stopLossPrice,
      takeProfitPrice,
      convictionLevel,
      duration,
    }: {
      positionId: number;
      thesisText: string;
      stopLossPrice?: number | null;
      takeProfitPrice?: number | null;
      convictionLevel?: number | null;
      duration?: 'Core' | 'Growth' | 'Speculative' | null;
    }) =>
      createPositionThesis(
        positionId,
        thesisText,
        stopLossPrice,
        takeProfitPrice,
        convictionLevel,
        duration
      ),
    onSuccess: (data, variables) => {
      // Invalidate and refetch thesis for this position
      queryClient.invalidateQueries({
        queryKey: ['positionThesis', variables.positionId],
      });
    },
  });
};

/**
 * Hook to update a position thesis.
 */
export const useUpdatePositionThesis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      thesisId,
      thesisData,
    }: {
      thesisId: number;
      thesisData: {
        thesis_text?: string;
        stop_loss_price?: number | null;
        take_profit_price?: number | null;
        conviction_level?: number | null;
        duration?: 'Core' | 'Growth' | 'Speculative' | null;
      };
    }) => updatePositionThesis(thesisId, thesisData),
    onSuccess: (data) => {
      // Invalidate and refetch thesis for position
      queryClient.invalidateQueries({
        queryKey: ['positionThesis', data.position_id],
      });
    },
  });
};

/**
 * Hook to delete a position thesis.
 */
export const useDeletePositionThesis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ thesisId, positionId }: { thesisId: number; positionId: number }) =>
      deletePositionThesis(thesisId),
    onSuccess: (data, variables) => {
      // Invalidate and refetch thesis for position
      queryClient.invalidateQueries({
        queryKey: ['positionThesis', variables.positionId],
      });
    },
  });
};

export const useWatchlistThesis = (watchlistItemId: number) =>
  useQuery({
    queryKey: ['watchlistThesis', watchlistItemId],
    queryFn: () => getWatchlistThesis(watchlistItemId),
    enabled: !!watchlistItemId,
  });

export const useAddWatchlistThesis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts: WatchlistThesisCreateRequest) => createWatchlistThesis(opts),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: ['watchlistThesis', variables.watchlist_item_id] }),
  });
};
